import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom, timeout, BehaviorSubject, throwError } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

import { AppConfigService } from '~/core/services/appconfig.service';
import { AppConfig } from '~/core/models/appconfig';
import { GraphService } from '~/core/services/graph.service';

const TIMEOUT: number = 30000;

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    private get config(): AppConfig { return this.appconfigService.getConfig(); }
    private apiHeader: HttpHeaders = new HttpHeaders({ "accept": "text/plain", "Content-Type": "application/json" });
    private tokenSubject = new BehaviorSubject<string | null>(null);
    public token$ = this.tokenSubject.asObservable();

    constructor(
        private http: HttpClient,
        private appconfigService: AppConfigService,
        private msalService: MsalService,
        private graphService: GraphService
    ) {
        console.log('[HttpService] Initializing service');
        // Load existing token from localStorage
        const savedToken = localStorage.getItem('wells_api_token');
        if (savedToken) {
            console.log('[HttpService] Found existing token in localStorage');
            this.tokenSubject.next(savedToken);
        } else {
            console.log('[HttpService] No existing token in localStorage');
        }
    }

    public Get<T>(endpoint: string, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.get<T>(`${this.config.apiUrl}/${endpoint}`, { headers: headers }).pipe(timeout(callTimeout));
    }

    public Post<T>(endpoint: string, body?: any, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.post<T>(`${this.config.apiUrl}/${endpoint}`, body, { headers: headers }).pipe(timeout(callTimeout));
    }

    public async GetAsset(file: string) {
        let results: any = null;

        try {
            let stream$ = this.http.get(`assets/${file}`);

            results = await lastValueFrom(stream$);
        } catch (error: any) {
            throw error;
        }

        return results;
    }

    public GetExternal<T>(url: string, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.get<T>(url).pipe(timeout(callTimeout));
    }

    //  Wells API Authentication Methods
    public issueWellsToken(email: string = 'test@example.com'): Observable<any> {
        console.log('[MSAL] issueWellsToken - Starting token acquisition');
        // Get the MSAL access token from the authenticated user
        const account = this.msalService.instance.getActiveAccount();
        
        if (!account) {
            console.error('[MSAL] issueWellsToken - No active MSAL account found');
            throw new Error('No user authenticated. Please log in with MSAL first.');
        }
        
        console.log('[MSAL] issueWellsToken - Active account found, requesting token silently');
        // Acquire token silently from MSAL
        const tokenRequest = {
            scopes: [`${this.config.clientId}/user_impersonation`],
            account: account
        };
        
        return new Observable(observer => {
            this.msalService.acquireTokenSilent(tokenRequest).subscribe({
                next: (response: any) => {
                    console.log('[MSAL] issueWellsToken - Token acquired successfully');
                    const msalJwtToken = response.accessToken;
                    
                    // Store token in localStorage for fallback
                    localStorage.setItem('wells_api_token', msalJwtToken);
                    this.tokenSubject.next(msalJwtToken);
                    
                    console.log('[MSAL] issueWellsToken - Fetching user roles from Graph API');
                    // Now fetch user roles from Graph API
                    this.fetchUserRolesFromGraph(msalJwtToken).subscribe({
                        next: (roles: any) => {
                            console.log('[MSAL] issueWellsToken - User roles fetched, count:', roles?.length ?? 0);
                            observer.next({ 
                                access_token: msalJwtToken,
                                roles: roles 
                            });
                        },
                        error: (error: any) => {
                            console.warn('[MSAL] issueWellsToken - Could not fetch roles from Graph, proceeding without roles. Status:', error?.status);
                            observer.next({ 
                                access_token: msalJwtToken,
                                roles: [] 
                            });
                        }
                    });
                },
                error: (error: any) => {
                    console.error('[MSAL] issueWellsToken - FAILED to acquire token. Error code:', error?.errorCode, '| Message:', error?.message);
                    observer.error(new Error('Failed to acquire MSAL token: ' + error.message));
                }
            });
        });
    }

    private fetchUserRolesFromGraph(msalJwtToken: string): Observable<any> {
        console.log('[MSAL] fetchUserRolesFromGraph - Fetching user roles via GraphService');
        return this.graphService.getMemberOf().pipe(
            map((groups: any[]) =>
                groups
                    .filter((entry: any) => entry.displayName)
                    .map((entry: any) => ({
                        id: entry.id,
                        displayName: entry.displayName,
                        type: entry['@odata.type']
                    }))
            ),
            catchError((error: any) => throwError(() => error))
        );
    }

    public setWellsToken(token: string): void {
        this.tokenSubject.next(token);
        localStorage.setItem('wells_api_token', token);
    }

    public getWellsToken(): string | null {
        // First check tokenSubject, then localStorage, then config.json
        return this.tokenSubject.value || localStorage.getItem('wells_api_token') || this.config.access_token || null;
    }

    public clearWellsToken(): void {
        this.tokenSubject.next(null);
        localStorage.removeItem('wells_api_token');
    }



     private getWellsApiHeaders(): HttpHeaders {
        // Get MSAL JWT token - stored in localStorage after acquireTokenSilent during login
        const msalJwtToken = localStorage.getItem('wells_api_token') || this.tokenSubject.value;
        
        // Build headers with token if available. Backend will validate token and return 401 if invalid.
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        });
        
        if (msalJwtToken) {
            headers = headers.set('Authorization', `Bearer ${msalJwtToken}`);
        }
        
        return headers;
    }

    public getWellsList(page: number = 1, pageSize: number = 20, search?: string): Observable<any> {
        console.log('[API] getWellsList - Starting, page:', page, '| search:', search || 'none');
        
        const headers = this.getWellsApiHeaders();

        // Build query parameters for pagination
        const params: { [key: string]: string } = {
            page: page.toString(),
            page_size: pageSize.toString()
        };
        
        // Add search parameter only if provided
        if (search && search.trim()) {
            params['search'] = search.trim();
        }
        
        // Get selected BU from sessionStorage to determine API endpoint
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        
        // Use old endpoint for ABU, new BU-prefixed endpoint for RBU
        const apiPath = selectedBU === 'RBU' 
            ? `${this.config.apiUrl}/api/rbu/wells_list`
            : `${this.config.apiUrl}/api/wells_list`;
        
        console.log('[API] getWellsList - BU:', selectedBU, '| Endpoint:', apiPath);
        return this.http.get(apiPath, { 
            headers,
            params
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Return full response with wells array and pagination info
                if (response && response.wells && Array.isArray(response.wells)) {
                    // Convert well names to objects with name property
                    const wellsArray = response.wells.map((well: any) => {
                        if (typeof well === 'string') {
                            return { name: well };
                        }
                        return well;
                    });
                    return {
                        wells: wellsArray,
                        pagination: response.pagination || null,
                        search: response.search || null
                    };
                } else if (Array.isArray(response)) {
                    // Handle legacy array response
                    const wellsArray = response.map((well: any) => {
                        if (typeof well === 'string') {
                            return { name: well };
                        }
                        return well;
                    });
                    return { wells: wellsArray, pagination: null, search: null };
                }
                // Return empty response as fallback
                return { wells: [], pagination: null, search: null };
            }),
            catchError((error: any) => {
                console.error('[API] getWellsList - FAILED. Status:', error?.status, '| Message:', error?.message);
                if (error.status === 401) {
                    console.error('[API] getWellsList - Token invalid/expired (401). User needs to log in again.');
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    public saveEngineerSuggestions(
        wellName: string, 
        schemaType: string, 
        userId: string, 
        suggestions: any[]
    ): Observable<any> {
        console.log('[API] saveEngineerSuggestions - Starting | well:', wellName, '| schema:', schemaType, '| records:', suggestions.length);
        
        const headers = this.getWellsApiHeaders();
        
        // Extract just the record data from each suggestion object
        const formattedSuggestions = suggestions.map(item => {
            // If the item has a 'record' property, use that directly
            return item.record || item;
        });
        
        // For direct array format (without any wrapper)
        const directArrayPayload = formattedSuggestions;
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        const apiPath = selectedBU === 'RBU'
            ? `${this.config.apiUrl}/api/rbu/suggestions`
            : `${this.config.apiUrl}/api/suggestions`;

        // First try with URL parameters and the direct array payload (no wrapper)
        const url = `${apiPath}?well_name=${encodeURIComponent(wellName)}&schema_type=${encodeURIComponent(schemaType)}&user_id=${encodeURIComponent(userId)}`;
        
        console.log('[API] saveEngineerSuggestions - BU:', selectedBU, '| Attempt 1: direct array payload');
        return this.http.post(url, directArrayPayload, { headers })
            .pipe(
                timeout(TIMEOUT),
                tap(() => console.log('[API] saveEngineerSuggestions - SUCCESS on attempt 1')),
                catchError(error => {
                    if (error.status === 401) {
                        console.error('[API] saveEngineerSuggestions - Token invalid/expired (401)');
                        this.clearWellsToken();
                        throw error;
                    }
                    
                    console.warn('[API] saveEngineerSuggestions - Attempt 1 failed (status:', error?.status, '), trying wrapped format');
                    
                    // Try with the wrapped format if direct array fails
                    const wrappedPayload = { suggestions: formattedSuggestions };
                    
                    return this.http.post(url, wrappedPayload, { headers }).pipe(
                        timeout(TIMEOUT),
                        tap(() => console.log('[API] saveEngineerSuggestions - SUCCESS on attempt 2')),
                        catchError(secondError => {
                            if (secondError.status === 401) {
                                console.error('[API] saveEngineerSuggestions - Token invalid/expired (401)');
                                this.clearWellsToken();
                                throw secondError;
                            }
                            
                            console.warn('[API] saveEngineerSuggestions - Attempt 2 failed (status:', secondError?.status, '), trying params format');
                            // Final attempt: try with payload in URL and direct array in body
                            return this.http.post(apiPath, directArrayPayload, { 
                                headers,
                                params: {
                                    well_name: wellName,
                                    schema_type: schemaType,
                                    user_id: userId
                                }
                            }).pipe(
                                timeout(TIMEOUT),
                                tap(() => console.log('[API] saveEngineerSuggestions - SUCCESS on attempt 3')),
                                catchError(thirdError => {
                                    if (thirdError.status === 401) {
                                        console.error('[API] saveEngineerSuggestions - Token invalid/expired (401)');
                                        this.clearWellsToken();
                                    }
                                    console.error('[API] saveEngineerSuggestions - ALL attempts FAILED. Final status:', thirdError?.status);
                                    throw thirdError;
                                })
                            );
                        })
                    );
                })
            );
    }

    // Combined method to authenticate and then get wells list
    public authenticateAndGetWells(email: string = 'test@example.com'): Observable<any> {
        
        this.clearWellsToken();
        
        return this.issueWellsToken(email).pipe(
            tap((tokenResponse: any) => {
                const token = this.getWellsToken();
                if (!token) {
                    throw new Error('Token was not properly stored after authentication');
                }
            }),
            switchMap((tokenResponse: any) => {
                const currentToken = this.getWellsToken();
                if (!currentToken) {
                    throw new Error('Token lost between authentication and wells request');
                }
                return this.getWellsList();
            }),
            catchError((error: any) => {
                this.clearWellsToken();
                throw error;
            })
        );
    }

    
    public testDirectWellsAPI(): Observable<any> {
        // Get MSAL account
        const account = this.msalService.instance.getActiveAccount();
        
        if (!account) {
            throw new Error('No user authenticated. Please log in with MSAL first.');
        }
        
        // Get MSAL token
        const tokenRequest = {
            scopes: [`${this.config.clientId}/user_impersonation`],
            account: account
        };
        
        return new Observable(observer => {
            this.msalService.acquireTokenSilent(tokenRequest).subscribe({
                next: (response: any) => {
                    const msalJwtToken = response.accessToken;
                    
                    // Store for later use
                    localStorage.setItem('wells_api_token', msalJwtToken);
                    this.tokenSubject.next(msalJwtToken);
                    
                    const headers = new HttpHeaders()
                        .set('Content-Type', 'application/json')
                        .set('Authorization', `Bearer ${msalJwtToken}`)
                        .set('Accept', 'application/json');
                    
                    const url = `${this.config.apiUrl}/api/wells_list`;
                    
                    this.http.get(url, { headers }).pipe(
                        timeout(TIMEOUT),
                        catchError((error: any) => throwError(() => error))
                    ).subscribe({
                        next: (result) => observer.next(result),
                        error: (err) => observer.error(err),
                        complete: () => observer.complete()
                    });
                },
                error: (error: any) => {
                    observer.error(new Error('Failed to acquire MSAL token: ' + error.message));
                }
            });
        });
    }

    public getSasUrl(blobPath: string): Observable<any> {
        console.log('[API] getSasUrl - Requesting SAS URL for path:', blobPath);
        
        const headers = this.getWellsApiHeaders();
        const trimmedPath = blobPath.trim();
        const url = `${this.config.apiUrl}/api/sas-url`;
        return this.http.get(url + `?file_path=${trimmedPath}`, { headers }).pipe(
            tap(() => console.log('[API] getSasUrl - SUCCESS')),
            catchError(error => {
                console.error('[API] getSasUrl - FAILED. Status:', error?.status);
                if (error.status === 401) {
                    console.error('[API] getSasUrl - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    /**
     * Fetch the list of user IDs from the API
     * GET /api/user_ids
     * response format : { user_ids: { user_id: string }[] }
     */
    public getUserIds(): Observable<{ user_ids: { user_id: string }[] }> {
        const headers = this.getWellsApiHeaders();
        return this.http.get<{ user_ids: { user_id: string }[] }>(`${this.config.apiUrl}/api/user_ids`, { headers }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                if (error.status === 401) {
                    console.error('[API] getUserIds - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    /**
     * Save a single record to the suggestions API
     * This method is specifically designed to update only one record at a time
     */
    public saveSingleRecord(
        wellName: string, 
        schemaType: string, 
        userId: string, 
        record: any
    ): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        const apiPath = selectedBU === 'RBU'
            ? `${this.config.apiUrl}/api/rbu/suggestions`
            : `${this.config.apiUrl}/api/suggestions`;
        
        // Create URL with parameters
        const url = `${apiPath}?well_name=${encodeURIComponent(wellName)}&schema_type=${encodeURIComponent(schemaType)}&user_id=${encodeURIComponent(userId)}`;
        
        // First approach: try sending just the record as a single object
        return this.http.post(url, record, { headers })
            .pipe(
                timeout(TIMEOUT),
                catchError(error => {
                    if (error.status === 401) {
                        console.error('[API] saveSingleRecord - Token invalid/expired (401)');
                        this.clearWellsToken();
                        throw error;
                    }
                    
                    // Second approach: try wrapping in an array
                    return this.http.post(url, [record], { headers }).pipe(
                        timeout(TIMEOUT),
                        catchError(secondError => {
                            if (secondError.status === 401) {
                                console.error('[API] saveSingleRecord - Token invalid/expired (401)');
                                this.clearWellsToken();
                                throw secondError;
                            }
                            
                            // Third approach: try with suggestions wrapper
                            const wrappedPayload = { suggestions: [record] };
                            
                            return this.http.post(url, wrappedPayload, { headers }).pipe(
                                timeout(TIMEOUT),
                                catchError(thirdError => {
                                    if (thirdError.status === 401) {
                                        console.error('[API] saveSingleRecord - Token invalid/expired (401)');
                                        this.clearWellsToken();
                                    }
                                    throw thirdError;
                                })
                            );
                        })
                    );
                })
            );
    }

    /**
     * Delete a single record using the API
     * This method is specifically designed to delete one record at a time
     */
    public deleteRecord(
        wellName: string, 
        schemaType: string, 
        userId: string, 
        recordId: string
    ): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        const apiPath = selectedBU === 'RBU'
            ? `${this.config.apiUrl}/api/rbu/suggestions`
            : `${this.config.apiUrl}/api/suggestions`;
        
        // Create URL with parameters for the DELETE request
        const url = `${apiPath}?well_name=${encodeURIComponent(wellName)}&schema_type=${encodeURIComponent(schemaType)}&user_id=${encodeURIComponent(userId)}&record_id=${encodeURIComponent(recordId)}`;
        
        // Make DELETE request to remove the record
        return this.http.delete(url, { headers })
            .pipe(
                timeout(TIMEOUT),
                catchError(error => {
                    if (error.status === 401) {
                        console.error('[API] deleteRecord - Token invalid/expired (401)');
                        this.clearWellsToken();
                        throw error;
                    }
                    
                    // Second approach: try with a different format if needed
                    if (error.status === 404 || error.status === 400) {
                        // Some APIs implement deletion via POST with an action parameter
                        return this.http.post(apiPath, 
                            { action: 'delete', record_id: recordId },
                            { 
                                headers,
                                params: {
                                    well_name: wellName,
                                    schema_type: schemaType,
                                    user_id: userId
                                }
                            }).pipe(
                                timeout(TIMEOUT),
                                catchError(secondError => {
                                    if (secondError.status === 401) {
                                        console.error('[API] deleteRecord - Token invalid/expired (401)');
                                        this.clearWellsToken();
                                    }
                                    throw secondError;
                                })
                            );
                    }
                    
                    throw error;
                })
            );
    }

    /**
     * Retrieves well files by type (DDR, DTR, WSS) with SAS URLs or by file name/path (RBU)
     * @param wellName The name of the well
     * @param fileType The type of file (ddr, dtr, wss) - for ABU
     * @param fileName Optional file name - for RBU
     * @param filePath Optional file path - for RBU
     * @param bu Optional business unit (ABU or RBU)
     * @returns Observable with list of matching file names and their SAS URLs
     */
    public getWellFiles(wellName: string, fileType?: string, fileName?: string, filePath?: string, bu?: string): Observable<any> {
        console.log('[API] getWellFiles - well:', wellName, '| type:', fileType || 'none', '| fileName:', fileName || 'none', '| bu:', bu || 'none');
        
        const headers = this.getWellsApiHeaders();
        const params: any = { well_name: wellName };
        
        // For RBU, use file_name and file_path
        if (bu && bu.toUpperCase() === 'RBU' && fileName && filePath) {
            params.file_name = fileName;
            params.file_path = filePath;
            params.bu = 'WY';
        } else if (fileType) {
            // For ABU, use file_type
            params.file_type = fileType;
        }
        
        return this.http.get(`${this.config.apiUrl}/api/well_files`, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            tap(() => console.log('[API] getWellFiles - SUCCESS')),
            catchError(error => {
                console.error('[API] getWellFiles - FAILED. Status:', error?.status);
                if (error.status === 401) {
                    console.error('[API] getWellFiles - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    /**
     * Get availability of well files (DDR, DTR, WSS) for a specific well
     * @param wellName The name of the well
     * @param bu Optional business unit (ABU or RBU)
     * @returns Observable with availability info including counts
     */
    public getWellFilesAvailability(wellName: string, bu?: string): Observable<any> {
        console.log('[API] getWellFilesAvailability - well:', wellName, '| bu:', bu || 'none');
        
        const headers = this.getWellsApiHeaders();
        const params: any = { well_name: wellName };
        
        // Add bu parameter if provided
        if (bu && bu.toUpperCase() === 'RBU') {
            params.bu = 'WY';
        }
        
        return this.http.get(`${this.config.apiUrl}/api/well_files/availability`, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            tap(() => console.log('[API] getWellFilesAvailability - SUCCESS')),
            catchError(error => {
                console.error('[API] getWellFilesAvailability - FAILED. Status:', error?.status);
                if (error.status === 401) {
                    console.error('[API] getWellFilesAvailability - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    /**
     * Upload an Excel file to the API
     * @param file The Excel file to upload (.xlsx or .xls)
     * @returns Observable with upload response
     */
    public uploadExcelFile(file: File): Observable<any> {
        // Validate file type
        const validExtensions = ['.xlsx', '.xls'];
        const fileName = file.name.toLowerCase();
        const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));
        
        if (!isValidFile) {
            return throwError(() => new Error('Invalid file type. Only Excel files (.xlsx, .xls) are allowed.'));
        }
        
        const token = this.getWellsToken();
        
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('excel_file', file, file.name);
        
        // Headers for multipart/form-data (don't set Content-Type, let browser set it with boundary)
        let headers = new HttpHeaders({
            'Accept': 'application/json'
        });
        
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }
        
        return this.http.post(`${this.config.apiUrl}/api/upload`, formData, { headers }).pipe(
            timeout(60000), // 60 seconds timeout for file uploads
            catchError(error => {
                console.error('Upload error:', error);
                
                if (error.status === 401) {
                    console.error('[API] uploadExcelFile - Token invalid/expired (401)');
                    this.clearWellsToken();
                    throw error;
                }
                
                // Extract error message from response
                let errorMessage = 'Upload failed';
                
                // Try different paths to get the error detail
                if (error?.error?.detail) {
                    errorMessage = error.error.detail;
                } else if (typeof error?.error === 'string') {
                    // Sometimes error.error is a JSON string
                    try {
                        const parsed = JSON.parse(error.error);
                        if (parsed.detail) {
                            errorMessage = parsed.detail;
                        }
                    } catch {
                        errorMessage = error.error;
                    }
                } else if (error?.message) {
                    errorMessage = error.message;
                } else if (error?.statusText) {
                    errorMessage = `${error.status}: ${error.statusText}`;
                }
                
                console.error('Extracted error message:', errorMessage);
                return throwError(() => new Error(errorMessage));
            })
        );
    }

    /**
     * Upload a RAW ZIP file to the API
     * @param file The ZIP file to upload (.zip)
     * @returns Observable with upload response containing job_id
     */
    public uploadRawFile(file: File): Observable<any> {
        // Validate file type
        const validExtensions = ['.zip'];
        const fileName = file.name.toLowerCase();
        const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));
        
        if (!isValidFile) {
            return throwError(() => new Error('Invalid file type. Only ZIP files (.zip) are allowed.'));
        }
        
        const token = this.getWellsToken();
        
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('zip_file', file, file.name);
        
        // Headers for multipart/form-data (don't set Content-Type, let browser set it with boundary)
        let headers = new HttpHeaders({
            'Accept': 'application/json'
        });
        
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }
        
        // Upload to /api/well-files/upload-zip endpoint with country parameter
        return this.http.post(`${this.config.apiUrl}/api/well-files/upload-zip?country=Wyoming`, formData, { headers }).pipe(
            timeout(60000), // 60 seconds timeout for file uploads
            catchError(error => {
                console.error('RAW file upload error:', error);
                
                if (error.status === 401) {
                    console.error('[API] uploadRawFile - Token invalid/expired (401)');
                    this.clearWellsToken();
                    throw error;
                }
                
                // Extract error message from response
                let errorMessage = 'RAW file upload failed';
                
                // Try different paths to get the error detail
                if (error?.error?.detail) {
                    errorMessage = error.error.detail;
                } else if (typeof error?.error === 'string') {
                    // Sometimes error.error is a JSON string
                    try {
                        const parsed = JSON.parse(error.error);
                        if (parsed.detail) {
                            errorMessage = parsed.detail;
                        }
                    } catch {
                        errorMessage = error.error;
                    }
                } else if (error?.message) {
                    errorMessage = error.message;
                } else if (error?.statusText) {
                    errorMessage = `${error.status}: ${error.statusText}`;
                }
                
                console.error('Extracted error message:', errorMessage);
                return throwError(() => new Error(errorMessage));
            })
        );
    }

    /**
     * Poll the upload status endpoint to check progress of file upload
     * @param jobId The job ID returned from uploadExcelFile or uploadRawFile
     * @returns Observable with upload status and progress information
     */
    public getUploadStatus(jobId: string): Observable<any> {
        if (!jobId) {
            return throwError(() => new Error('No job ID provided'));
        }
        
        const headers = this.getWellsApiHeaders();
        // Use longer timeout for status checks - the server might be computing progress
        const statusTimeoutMs = 120000; // 2 minutes for status polling (vs 30s default)
        return this.http.get(`${this.config.apiUrl}/api/upload/status/${jobId}`, { headers }).pipe(
            timeout(statusTimeoutMs),
            catchError(error => {
                console.error('Error checking upload status:', error);
                if (error.status === 401) {
                    console.error('[API] getUploadStatus - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    /**
     * Get most recent engineer suggestions logs for a particular well and schema
     * 
     * @param wellName The name of the well
     * @param schemaType The schema type
     * @returns Observable with the latest log file content if available
     */
    public getFileTemplateSample(): Observable<any> {
        const headers = this.getWellsApiHeaders();
        return this.http.get(`${this.config.apiUrl}/api/file_template_sample`, { headers }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('Error getting file template sample:', error);
                if (error.status === 401) {
                    console.error('[API] getFileTemplateSample - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                throw error;
            })
        );
    }

    /**
     * Get most recent engineer suggestions logs for a particular well and schema
     * 
     * @param wellName The name of the well
     * @param schemaType The schema type
     * @returns Observable with the latest log file content if available
     */
    public getEngineerSuggestionsLogs(wellName: string, schemaType: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName, 
            schema_type: schemaType 
        };
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        const apiPath = selectedBU === 'RBU'
            ? `${this.config.apiUrl}/api/rbu/suggestions/logs`
            : `${this.config.apiUrl}/api/suggestions/logs`;
        
        return this.http.get(apiPath, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Determine if the response is an array or an object with logs property
                let logsArray: any[] = [];
                
                if (Array.isArray(response)) {
                    logsArray = [...response];
                } else if (response && response.logs && Array.isArray(response.logs)) {
                    logsArray = [...response.logs];
                } else if (response && typeof response === 'object') {
                    // Try to extract any array-like data from the response
                    const possibleArrays = Object.values(response).filter(value => Array.isArray(value));
                    if (possibleArrays.length > 0) {
                        // Use the first array found in the response
                        logsArray = [...possibleArrays[0]];
                    } else {
                        return { logs: [] };
                    }
                } else {
                    // Return standardized format with empty logs
                    return { logs: [] };
                }
                
                // Preserve exact fields from the API response without transformations
                // Only wrap in a logs property if not already present
                const processedResponse = { logs: logsArray };
                return processedResponse;
            }),
            catchError(error => {
                if (error.status === 401) {
                    console.error('[API] getEngineerSuggestionsLogs - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Get change logs for a particular well and schema
     */
    public getChangeLogs(wellName: string, schemaType: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName, 
            schema_type: schemaType 
        };
        
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        const apiPath = selectedBU === 'RBU'
            ? `${this.config.apiUrl}/api/rbu/change-logs`
            : `${this.config.apiUrl}/api/change-logs`;

        return this.http.get(apiPath, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Process the response to ensure it's in the expected format
                if (Array.isArray(response)) {
                    return response;
                } else if (response && response.logs && Array.isArray(response.logs)) {
                    return response.logs;
                } else if (response && typeof response === 'object') {
                    // Try to find any property that contains an array of logs
                    const possibleArrays = Object.values(response).filter(val => Array.isArray(val));
                    if (possibleArrays.length > 0) {
                        return possibleArrays[0];
                    }
                }
                
                // Return empty array as fallback
                return [];
            }),
            catchError(error => {
                if (error.status === 401) {
                    console.error('[API] getChangeLogs - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Get all engineer suggestions logs for a particular well
     * 
     * @param wellName The name of the well
     * @returns Observable with all available log entries for the well
     */
    public getRecentEngineerSuggestionsLogs(wellName: string): Observable<any> {
        const headers = this.getWellsApiHeaders();
        const params = { 
            well_name: wellName
        };
        const selectedBU = sessionStorage.getItem('selectedBU') || 'ABU';
        const apiPath = selectedBU === 'RBU'
            ? `${this.config.apiUrl}/api/rbu/suggestions/logs`
            : `${this.config.apiUrl}/api/suggestions/logs`;
        
        return this.http.get(apiPath, { 
            headers, 
            params 
        }).pipe(
            timeout(TIMEOUT),
            map((response: any) => {
                // Determine if the response is an array or an object with logs property
                let logsArray: any[] = [];
                
                if (Array.isArray(response)) {
                    logsArray = [...response];
                } else if (response && response.logs && Array.isArray(response.logs)) {
                    logsArray = [...response.logs];
                } else if (response && typeof response === 'object') {
                    // Try to extract any array-like data from the response
                    const possibleArrays = Object.values(response).filter(value => Array.isArray(value));
                    if (possibleArrays.length > 0) {
                        // Use the first array found in the response
                        logsArray = [...possibleArrays[0]];
                    } else {
                        return { logs: [] };
                    }
                } else {
                    // Return standardized format with empty logs
                    return { logs: [] };
                }
                
                // Preserve exact fields from the API response without transformations
                // Only wrap in a logs property if not already present
                const processedResponse = { logs: logsArray };
                return processedResponse;
            }),
            catchError(error => {
                if (error.status === 401) {
                    console.error('[API] getRecentEngineerSuggestionsLogs - Token invalid/expired (401)');
                    this.clearWellsToken();
                }
                return throwError(() => error);
            })
        );
    }
}
